/**
 * Landlord/superagent dashboard — `GET reports/dashboard/`, same query key
 * (`['reports-dashboard']`) `ReportsDashboardPage.tsx` uses so TanStack Query
 * dedupes if a user visits both. This route only mounts for LANDLORD/
 * SUPERAGENT (router.tsx), so the manager response shape is the only one
 * reachable here — `isTenantDashboard` still guards it to satisfy the
 * `Dashboard` union type, not because the tenant branch is really reachable.
 *
 * Icon-badged stat tiles (`StatTile`) plus a "Needs attention" panel
 * (`DashboardPanel`) surfacing overdue invoices and urgent maintenance —
 * Flutter-parity with `landlord_dashboard.dart`'s metric-card grid + "Urgent
 * Overdue Payments"/"Management Actions" panels, ported as data (not the
 * exact widget set) since this page stays deliberately lighter than
 * ReportsDashboardPage's full `<dl>`-per-metric layout.
 */
import { useQuery } from '@tanstack/react-query';
import { Column, Grid } from '@carbon/react';
import { Building, Home, UserMultiple, Money, ToolBox, Receipt } from '@carbon/icons-react';
// DashboardPanel's `emptyIcon` is still typed `LucideIcon` — its own Carbon
// conversion is deferred to Phase 3 (see CARBON_MIGRATION.md), so this one
// icon stays lucide until then rather than half-converting that component.
import { CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { getDashboard } from '../../api/endpoints/reports';
import { listPayments, getOverdueInvoices } from '../../api/endpoints/payments';
import { listMaintenanceRequests } from '../../api/endpoints/maintenance';
import { isTenantDashboard } from '../../types/report';
import { StatTile } from '../../components/StatTile';
import { DashboardPanel, type DashboardPanelRow } from '../../components/DashboardPanel';
import type { PaymentListRow } from '../../types/payment';

/**
 * Rent banked in the CURRENT calendar month.
 *
 * `reports/dashboard/` has no such figure, and `payments/payments/` has no
 * date filter and no `page_size` override (PAGE_SIZE is a fixed 20, see
 * src/api/types.ts), so the only correct answer walks every page and filters
 * client-side. That costs a few extra requests by design — reading page 1 alone
 * would silently under-report as soon as a landlord has 20+ payments on file.
 *
 * `amount` is a decimal STRING everywhere else in this app for good reason
 * (src/types/payment.ts). Coercing to Number here is acceptable ONLY because
 * this is a display-level dashboard approximation — never copy this into
 * anything that settles a balance or writes the ledger.
 */
async function fetchRentCollectedThisMonth(): Promise<number> {
  const rows: PaymentListRow[] = [];

  for (let page = 1; ; page += 1) {
    const response = await listPayments({ page });
    rows.push(...response.results);
    // The DRF envelope's own end-of-list signal; an empty page ends it too, so
    // a backend that returns `next` on a trailing empty page cannot loop us.
    if (response.results.length === 0 || response.next === null) break;
  }

  const now = new Date();

  return rows.reduce((total, row) => {
    if (row.status !== 'SUCCESS') return total;
    const paidAt = new Date(row.payment_date);
    if (paidAt.getFullYear() !== now.getFullYear() || paidAt.getMonth() !== now.getMonth()) {
      return total;
    }
    return total + Number(row.amount);
  }, 0);
}

export function LandlordHome(): React.JSX.Element {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['reports-dashboard'],
    queryFn: () => getDashboard(),
  });

  const collectedThisMonth = useQuery({
    queryKey: ['payments-collected-this-month'],
    queryFn: fetchRentCollectedThisMonth,
  });

  const overdueInvoices = useQuery({
    queryKey: ['overdue-invoices'],
    queryFn: () => getOverdueInvoices(),
  });

  const urgentMaintenance = useQuery({
    queryKey: ['maintenance-requests', { priority: 'URGENT' }],
    queryFn: () => listMaintenanceRequests({ priority: 'URGENT' }),
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const dashboard = query.data;
  if (isTenantDashboard(dashboard)) return <></>;

  const openMaintenance = dashboard.maintenance.new + dashboard.maintenance.in_progress;

  const overdueRows: DashboardPanelRow[] = (overdueInvoices.data ?? []).map((invoice) => ({
    id: invoice.id,
    to: `/landlord/rent/${invoice.id}`,
    label: `${invoice.tenant_name} — ${invoice.unit_number}`,
    value: `${invoice.amount_due} due ${invoice.due_date}`,
  }));

  const urgentRows: DashboardPanelRow[] = (urgentMaintenance.data?.results ?? []).map((request) => ({
    id: request.id,
    to: `/landlord/maintenance/${request.id}`,
    label: request.title,
    value: `${request.property_name} — ${request.unit_number}`,
  }));

  return (
    <section className="landlord-home">
      <h2>Landlord Dashboard</h2>
      <div className="dashboard-stats">
        <Grid withRowGap>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Building}
              to="/landlord/properties"
              value={dashboard.properties.total}
              label="Properties"
              detail={
                user?.role === 'SUPERAGENT' && dashboard.properties.pending_approval > 0
                  ? `${dashboard.properties.pending_approval} pending approval`
                  : undefined
              }
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Home}
              to="/landlord/properties"
              value={dashboard.units.vacant}
              label="Vacant units"
              detail={`${dashboard.units.occupied} occupied`}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={UserMultiple}
              to="/landlord/tenants/leases"
              value={dashboard.leases.active}
              label="Active leases"
              detail={
                dashboard.leases.expiring_soon > 0
                  ? `${dashboard.leases.expiring_soon} expiring soon`
                  : undefined
              }
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Money}
              to="/landlord/rent/overdue"
              value={dashboard.payments.overdue}
              label="Overdue invoices"
              detail={`${dashboard.payments.overdue_amount} overdue`}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={ToolBox}
              to="/landlord/maintenance"
              value={openMaintenance}
              label="Open maintenance requests"
              detail={dashboard.maintenance.urgent > 0 ? `${dashboard.maintenance.urgent} urgent` : undefined}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Money}
              to="/landlord/rent/payments"
              value={collectedThisMonth.isPending ? '…' : (collectedThisMonth.data ?? '—')}
              label="Rent collected this month"
              detail={collectedThisMonth.isError ? 'Could not load' : undefined}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile icon={Money} to="/landlord/rent" value={dashboard.payments.total_due} label="Total due" />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Receipt}
              to="/landlord/expenses"
              value={dashboard.expenses.this_month}
              label="Expenses this month"
            />
          </Column>
        </Grid>
      </div>

      <DashboardPanel
        title="Overdue payments"
        rows={overdueRows}
        emptyIcon={CheckCircle2}
        emptyMessage="All payments are up to date!"
      />

      <DashboardPanel
        title="Urgent maintenance"
        rows={urgentRows}
        emptyIcon={CheckCircle2}
        emptyMessage="No urgent maintenance requests."
      />
    </section>
  );
}

export default LandlordHome;
